import * as React from 'react';
import { keygen } from '../../utils';
import { Button, ButtonProps } from '../../components/Button';
import { IconButton } from '../../components/IconButton';
import { Tooltip, TooltipProps } from '../../components/Tooltip';
import { buttonTypes, ButtonType } from './button.types';

export type ButtonBlueprint = {
  type: ButtonType;
  onClick: ButtonProps['onClick'];
  // TODO: this should be required to ensure that the button isn't recreated every re-render
  id?: number | string;
  isExpansionPanelIcon?: boolean;
  isActionIcon?: boolean;
  disabled?: boolean;
  classes?: any;
  actionType?: ButtonProps['color'];
  size?: ButtonProps['size'];
  tooltipTitle?: TooltipProps['title'];
  toolTipPosition?: TooltipProps['placement'];
  translate?: any;
  dataProp?: string;
  isIconButton?: boolean;
  showIcon?: boolean;
  showText?: boolean;
  text?: string;
  label?: string;
};

const createButtonProps = (type: ButtonType, showIcon: boolean, translate: any) => {
  const buttonType = buttonTypes[type];
  const baseTypeConfig = { text: translate(buttonType.text), label: translate(buttonType.text) };

  if ('isActionIcon' in buttonType || 'isExpansionPanelIcon' in buttonType) {
    return { ...baseTypeConfig, actionIconType: type, isExpansionPanelIconType: type, icon: showIcon ? buttonType.icon : undefined };
  }

  return { ...baseTypeConfig, icon: showIcon ? buttonType.icon : undefined };
};

const createButton = ({
  type,
  translate,
  isIconButton,
  showIcon = !!isIconButton,
  showText = true,
  tooltipTitle,
  actionType = 'secondary',
  size = 'medium',
  toolTipPosition,
  classes,
  text,
  ...rest
}: ButtonBlueprint) => {
  const props = { ...createButtonProps(type, showIcon, translate), color: actionType, size, ...rest };
  const { id, ...restProps } = props;
  /* istanbul ignore next */
  if (process.env.NODE_ENV !== 'production') {
    checkInvalidButtonId(id);
  }
  const key = id ?? keygen();
  const button = (
    <Button key={key} className={classes} id={id ? String(id) : undefined} {...restProps}>
      {showText ? text || restProps.text : ''}
    </Button>
  );

  const iconButton = <IconButton key={key} className={classes} id={id ? String(id) : undefined} fontSize={size} component="span" {...restProps} />;

  // Pick the component
  const component = isIconButton ? iconButton : button;
  return tooltipTitle || isIconButton ? (
    <Tooltip title={tooltipTitle || text || restProps.text} placement={toolTipPosition} key={key}>
      {component}
    </Tooltip>
  ) : (
    component
  );
};

/* Utilities */
let hasCheckInvalidButtonIdRun = false;
/* istanbul ignore next */
function checkInvalidButtonId(id: string | number | undefined) {
  if (id === undefined && !hasCheckInvalidButtonIdRun) {
    console.warn(
      `Please make sure to pass an id to button factory, otherwise the button will unmount / remount on every re-render instead of updating in place.`
    );
    hasCheckInvalidButtonIdRun = true;
  }
}

export { createButton };
